import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Plus, Search, User, Users, Pencil, Trash2, ChevronDown, Check, ArrowRight, Upload, Download, FileText, Image as ImageIcon, X, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { logFileDownload } from '@/components/ActivityTracker';
import { CompanyPriceList } from '@/components/CompanyPriceList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company color palettes
const COMPANY_COLORS = [
  { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', accent: '#3B82F6' },
  { bg: 'from-emerald-500 to-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', accent: '#10B981' },
  { bg: 'from-violet-500 to-violet-600', light: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', accent: '#8B5CF6' },
  { bg: 'from-amber-500 to-amber-600', light: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', accent: '#F59E0B' },
  { bg: 'from-rose-500 to-rose-600', light: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', accent: '#F43F5E' },
  { bg: 'from-cyan-500 to-cyan-600', light: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', accent: '#06B6D4' },
];

// Add Employee Modal
const AddEmployeeModal = ({ isOpen, onClose, onSave, isRTL, t, existingEmployees = [] }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [phone, setPhone] = useState('');

  const duplicates = name.trim().length >= 2
    ? existingEmployees.filter(emp => {
        const inputWords = name.trim().split(/\s+/).filter(w => w.length >= 2);
        if (inputWords.length < 2) return false;
        const empName = emp.name.trim();
        const inputSeq = inputWords.join(' ');
        return empName.includes(inputSeq) || inputSeq.includes(empName);
      })
    : [];

  const handleSave = () => {
    if (!name.trim()) { toast.error(t('error')); return; }
    onSave({ name, passport_number: passportNumber, nationality, phone });
    setName(''); setPassportNumber(''); setNationality(''); setPhone('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
        <div className={`flex items-center gap-2.5 mb-5`}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>
            <User className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">{t('addEmployee')}</h2>
        </div>
        <div className={`space-y-4`}>
          <div>
            <label className="text-sm font-medium text-slate-600">{t('employeeName')} <span className="text-red-500">*</span></label>
            <Input data-testid="add-employee-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-10" />
            {duplicates.length > 0 && (
              <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="duplicate-name-warning">
                <p className="text-xs font-medium text-amber-700 mb-1.5">
                  {isRTL ? `يوجد تشابه مع موظفين في المنشأة:` : `Similar employees found:`}
                </p>
                {duplicates.map(dup => (
                  <button key={dup.id} data-testid={`duplicate-link-${dup.id}`}
                    onClick={() => { onClose(); navigate(`/employees/${dup.id}`); }}
                    className={`flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-900 underline cursor-pointer w-full ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                    <User className="w-3 h-3 shrink-0" />
                    <span>{dup.name}</span>
                    {dup.passport_number && <span className="text-amber-500 font-mono" dir="ltr">({dup.passport_number})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{t('passportNumber')}</label>
            <Input data-testid="add-employee-passport" value={passportNumber} onChange={(e) => setPassportNumber(e.target.value)} className="mt-1.5 h-10" dir="ltr" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'الجنسية' : 'Nationality'}</label>
            <Input data-testid="add-employee-nationality" value={nationality} onChange={(e) => setNationality(e.target.value)} className="mt-1.5 h-10" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'رقم الهاتف' : 'Phone Number'}</label>
            <Input data-testid="add-employee-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-10" dir="ltr" />
          </div>
          <div className={`flex gap-3 pt-3`}>
            <Button data-testid="save-employee-btn" onClick={handleSave} className="flex-1 h-10"
              style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>{t('save')}</Button>
            <Button variant="outline" onClick={onClose} className="flex-1 h-10">{t('cancel')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Employee Modal
const EditEmployeeModal = ({ isOpen, onClose, onSave, employee, isRTL, t }) => {
  const [name, setName] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (employee) {
      setName(employee.name || '');
      setPassportNumber(employee.passport_number || '');
      setNationality(employee.nationality || '');
      setPhone(employee.phone || '');
    }
  }, [employee]);

  const handleSave = () => {
    if (!name.trim()) { toast.error(t('error')); return; }
    onSave({ name, passport_number: passportNumber, nationality, phone });
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
        <div className={`flex items-center gap-2.5 mb-5`}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>
            <Pencil className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">{isRTL ? 'تعديل الموظف' : 'Edit Employee'}</h2>
        </div>
        <div className={`space-y-4`}>
          <div>
            <label className="text-sm font-medium text-slate-600">{t('employeeName')} <span className="text-red-500">*</span></label>
            <Input data-testid="edit-employee-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-10" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{t('passportNumber')}</label>
            <Input data-testid="edit-employee-passport" value={passportNumber} onChange={(e) => setPassportNumber(e.target.value)} className="mt-1.5 h-10" dir="ltr" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'الجنسية' : 'Nationality'}</label>
            <Input data-testid="edit-employee-nationality" value={nationality} onChange={(e) => setNationality(e.target.value)} className="mt-1.5 h-10" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">{isRTL ? 'رقم الهاتف' : 'Phone Number'}</label>
            <Input data-testid="edit-employee-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-10" dir="ltr" />
          </div>
          <div className={`flex gap-3 pt-3`}>
            <Button data-testid="save-edit-employee-btn" onClick={handleSave} className="flex-1 h-10"
              style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>{t('save')}</Button>
            <Button variant="outline" onClick={onClose} className="flex-1 h-10">{t('cancel')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirm Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, employeeName, isRTL, t }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 z-10">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-2">{isRTL ? 'تأكيد الحذف' : 'Confirm Delete'}</h2>
          <p className="text-sm text-slate-500 mb-5">
            {isRTL ? `هل أنت متأكد من حذف الموظف "${employeeName}"؟` : `Delete "${employeeName}"?`}
          </p>
          <div className={`flex gap-3`}>
            <Button onClick={onConfirm} className="flex-1 h-10 bg-red-500 hover:bg-red-600 text-white">{isRTL ? 'حذف' : 'Delete'}</Button>
            <Button variant="outline" onClick={onClose} className="flex-1 h-10">{t('cancel')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Employee Photo component with fallback
const EmployeePhoto = ({ employeeId, employeeName, size = 'md' }) => {
  const [hasPhoto, setHasPhoto] = useState(true);
  const sizeClasses = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-14 h-14 text-lg',
    lg: 'w-20 h-20 text-2xl',
  };
  const initials = (employeeName || 'U').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

  if (!hasPhoto) {
    return (
      <div className={`${sizeClasses[size]} rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center font-bold text-slate-400 shrink-0`}>
        {initials}
      </div>
    );
  }

  return (
    <img
      src={`${API}/employees/${employeeId}/download/photo`}
      alt={employeeName}
      className={`${sizeClasses[size]} rounded-xl object-cover shrink-0 bg-slate-100`}
      onError={() => setHasPhoto(false)}
    />
  );
};

export const Employees = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, user, isClient, token } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [companyTransactions, setCompanyTransactions] = useState([]);
  const [companyTxExpanded, setCompanyTxExpanded] = useState(false);
  const [companyFiles, setCompanyFiles] = useState({});
  const [companyFilesExpanded, setCompanyFilesExpanded] = useState(false);
  const [showCompanyCustomUpload, setShowCompanyCustomUpload] = useState(false);
  const [companyCustomFileName, setCompanyCustomFileName] = useState('');
  const [companyUploading, setCompanyUploading] = useState(false);
  const companyFileInputRef = useRef(null);
  const [employeeCounts, setEmployeeCounts] = useState({});

  const isRTL = i18n.language === 'ar';

  useEffect(() => { fetchCompanies(); }, [isClient, user]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      let data = response.data;
      if (isClient && user?.client_id) {
        data = data.filter(c => c.client_id === user.client_id);
      }
      setCompanies(data);

      // Fetch employee counts for each company
      const counts = {};
      await Promise.all(data.map(async (c) => {
        try {
          const res = await axios.get(`${API}/employees?company_id=${c.id}`);
          counts[c.id] = res.data.length;
        } catch { counts[c.id] = 0; }
      }));
      setEmployeeCounts(counts);

      // Auto-select company from URL parameter
      const companyParam = searchParams.get('company');
      if (companyParam && data.length > 0) {
        const found = data.find(c => c.id === companyParam);
        if (found) {
          setSelectedCompany(found);
          await fetchEmployees(found.id);
        }
      }
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchEmployees = async (companyId) => {
    try {
      const response = await axios.get(`${API}/employees?company_id=${companyId}`);
      setEmployees(response.data);
      const txRes = await axios.get(`${API}/company-transactions?company_id=${companyId}`);
      setCompanyTransactions(txRes.data);
      const compRes = await axios.get(`${API}/companies/${companyId}`);
      setCompanyFiles(compRes.data.files || {});
    } catch (error) { console.error('Error:', error); }
  };

  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleCompanyFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !selectedCompany) return;
    const baseName = companyCustomFileName.trim();
    setCompanyUploading(true);
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) { toast.error(isRTL ? `${file.name} - حجم الملف كبير` : `${file.name} - File too large`); continue; }
      let displayName;
      if (baseName) { displayName = files.length === 1 ? baseName : `${baseName} ${i + 1}`; }
      else { displayName = file.name.replace(/\.[^/.]+$/, ''); }
      const fileKey = `custom_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`;
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file);
        });
        await axios.post(`${API}/companies/${selectedCompany.id}/upload`, { file_type: fileKey, file_data: dataUrl, file_name: file.name, display_name: displayName }, { headers: authHeaders });
        successCount++;
      } catch { toast.error(isRTL ? `فشل رفع: ${file.name}` : `Failed: ${file.name}`); }
    }
    setCompanyUploading(false);
    if (successCount > 0) { toast.success(isRTL ? `تم رفع ${successCount} ملف بنجاح` : `${successCount} file(s) uploaded`); fetchEmployees(selectedCompany.id); }
    setCompanyCustomFileName(''); setShowCompanyCustomUpload(false);
    if (companyFileInputRef.current) companyFileInputRef.current.value = '';
  };

  const handleCompanyFileDownload = async (fileType, fileName) => {
    try {
      const response = await axios.get(`${API}/companies/${selectedCompany.id}/download/${fileType}`, { responseType: 'blob', headers: authHeaders });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', fileName || `${fileType}.pdf`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(isRTL ? 'تم تحميل الملف' : 'File downloaded');
      logFileDownload(fileName || fileType, `ملف منشأة: ${selectedCompany?.name}`);
    } catch { toast.error(isRTL ? 'خطأ في تحميل الملف' : 'Error downloading file'); }
  };

  const handleCompanyFileDelete = async (fileType) => {
    if (!window.confirm(isRTL ? 'هل تريد حذف هذا الملف؟' : 'Delete this file?')) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/files/${fileType}`, { headers: authHeaders });
      toast.success(isRTL ? 'تم حذف الملف' : 'File deleted');
      fetchEmployees(selectedCompany.id);
    } catch { toast.error(isRTL ? 'خطأ في حذف الملف' : 'Error deleting file'); }
  };

  const handleSelectCompany = async (company) => {
    setSelectedCompany(company);
    setSearchQuery('');
    setCompanyTxExpanded(false);
    setCompanyFilesExpanded(false);
    await fetchEmployees(company.id);
  };

  const handleSaveEmployee = async (data) => {
    try {
      await axios.post(`${API}/employees`, { ...data, company_id: selectedCompany.id, establishment_id: selectedCompany.id });
      toast.success(t('success'));
      setShowAddEmployee(false);
      await fetchEmployees(selectedCompany.id);
      setEmployeeCounts(prev => ({ ...prev, [selectedCompany.id]: (prev[selectedCompany.id] || 0) + 1 }));
    } catch { toast.error(t('error')); }
  };

  const handleEditEmployee = async (data) => {
    try {
      await axios.put(`${API}/employees/${selectedEmployee.id}`, data);
      toast.success(t('success'));
      setShowEditEmployee(false); setSelectedEmployee(null);
      await fetchEmployees(selectedCompany.id);
    } catch { toast.error(t('error')); }
  };

  const handleDeleteEmployee = async () => {
    try {
      await axios.delete(`${API}/employees/${selectedEmployee.id}`);
      toast.success(t('success'));
      setShowDeleteConfirm(false); setSelectedEmployee(null);
      await fetchEmployees(selectedCompany.id);
      setEmployeeCounts(prev => ({ ...prev, [selectedCompany.id]: Math.max(0, (prev[selectedCompany.id] || 1) - 1) }));
    } catch { toast.error(t('error')); }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--theme-accent)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header with back button when company selected */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {selectedCompany ? selectedCompany.name : (isRTL ? 'المنشآت والموظفين' : 'Companies & Employees')}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {selectedCompany
              ? `${filteredEmployees.length} ${isRTL ? 'موظف' : 'employees'}`
              : `${companies.length} ${isRTL ? 'منشأة مسجلة' : 'registered companies'}`
            }
          </p>
        </div>
        {selectedCompany && (
          <Button
            variant="outline"
            onClick={() => { setSelectedCompany(null); setEmployees([]); setSearchQuery(''); }}
            className="gap-2 h-9"
            data-testid="back-to-companies-btn"
          >
            {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowRight className="w-4 h-4 rotate-180" />}
            {isRTL ? 'جميع المنشآت' : 'All Companies'}
          </Button>
        )}
      </div>

      {/* ============ COMPANIES GRID VIEW ============ */}
      {!selectedCompany && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
          {companies.map((company, index) => {
            const color = COMPANY_COLORS[index % COMPANY_COLORS.length];
            const empCount = employeeCounts[company.id] || 0;
            return (
              <div
                key={company.id}
                data-testid={`company-card-${company.id}`}
                onClick={() => handleSelectCompany(company)}
                className="group cursor-pointer rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300"
              >
                {/* Color strip */}
                <div className={`h-2 bg-gradient-to-r ${color.bg}`} />
                <div className="p-5">
                  <div className={`flex items-start gap-4`}>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color.bg} flex items-center justify-center shrink-0 shadow-sm`}>
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div className={`flex-1 min-w-0`}>
                      <h3 className="font-bold text-slate-800 text-base truncate group-hover:text-slate-900 transition-colors">
                        {company.name}
                      </h3>
                      <div className={`flex items-center gap-1.5 mt-1.5`}>
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-sm text-slate-500">{empCount} {isRTL ? 'موظف' : 'employees'}</span>
                      </div>
                    </div>
                  </div>
                  {/* Footer with arrow */}
                  <div className={`flex items-center justify-between mt-4 pt-3 border-t border-slate-100`}>
                    <span className={`text-xs font-medium ${color.text}`}>
                      {isRTL ? 'عرض الموظفين' : 'View Employees'}
                    </span>
                    <div className={`w-7 h-7 rounded-lg ${color.light} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      {isRTL
                        ? <ArrowRight className={`w-3.5 h-3.5 rotate-180`} style={{ color: color.accent }} />
                        : <ArrowRight className="w-3.5 h-3.5" style={{ color: color.accent }} />
                      }
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============ EMPLOYEES VIEW (when company selected) ============ */}
      {selectedCompany && (
        <div className="space-y-4">
          {/* Action bar */}
          <div className={`flex items-center gap-3 flex-wrap`}>
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
              <Input
                data-testid="employee-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('searchEmployee')}
                className={`h-10 bg-white border-slate-200 ${isRTL ? 'pr-10' : 'pl-10'}`}
              />
            </div>
            {isAdmin && (
              <Button
                data-testid="add-employee-btn"
                onClick={() => setShowAddEmployee(true)}
                className="h-10 px-5 gap-2"
                style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}
              >
                <Plus className="w-4 h-4" />
                {t('addEmployee')}
              </Button>
            )}
          </div>

          {/* Company Transactions - Collapsible */}
          {companyTransactions.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div
                className={`px-4 py-3 cursor-pointer select-none hover:bg-slate-50/50 transition-colors`}
                dir={isRTL ? 'rtl' : 'ltr'}
                onClick={() => setCompanyTxExpanded(!companyTxExpanded)}
                data-testid="company-tx-toggle"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center">
                      <FileText className="w-3.5 h-3.5 text-amber-600" />
                    </div>
                    {isRTL ? 'معاملات المنشأة' : 'Company Transactions'} ({companyTransactions.length})
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-slate-700" dir="ltr">
                      {companyTransactions.reduce((s, t) => s + (t.price || 0), 0).toFixed(0)} AED
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${companyTxExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </div>
              {companyTxExpanded && (
                <div className="px-4 pb-3 space-y-1.5 border-t border-slate-100 pt-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {companyTransactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${tx.status === 'completed' ? 'bg-emerald-500' : tx.status === 'pending' ? 'bg-amber-500' : 'bg-red-500'}`} />
                        <span className="text-slate-700 font-medium">{tx.name || (isRTL ? 'معاملة' : 'Transaction')}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-400 text-xs" dir="ltr">{new Date(tx.work_date).toLocaleDateString('en-GB')}</span>
                        <span className="font-semibold text-slate-800" dir="ltr">{tx.price?.toFixed(0)} AED</span>
                        {isAdmin && (
                          <button type="button" onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(isRTL ? `حذف معاملة المنشأة «${tx.name}» نهائياً من كل الأماكن؟` : `Permanently delete company transaction "${tx.name}" everywhere?`)) return;
                            try {
                              await axios.delete(`${API}/company-transactions/${tx.id}`);
                              setCompanyTransactions(prev => prev.filter(x => x.id !== tx.id));
                              toast.success(isRTL ? 'تم حذف المعاملة' : 'Transaction deleted');
                            } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
                          }} className="p-1 rounded-md text-red-500 hover:bg-red-50" title={isRTL ? 'حذف' : 'Delete'} data-testid={`company-tx-delete-${tx.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Company Files - Collapsible */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div
              className={`px-4 py-3 cursor-pointer select-none hover:bg-slate-50/50 transition-colors`}
              dir={isRTL ? 'rtl' : 'ltr'}
              onClick={() => setCompanyFilesExpanded(!companyFilesExpanded)}
              data-testid="company-files-toggle"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center">
                    <FolderOpen className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  {isRTL ? 'ملفات المنشأة' : 'Company Files'} ({Object.keys(companyFiles).length})
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${companyFilesExpanded ? 'rotate-180' : ''}`} />
              </div>
            </div>
            {companyFilesExpanded && (
              <div className="px-4 pb-3 space-y-2 border-t border-slate-100 pt-2" dir={isRTL ? 'rtl' : 'ltr'}>
                {Object.keys(companyFiles).length > 0 ? (
                  Object.entries(companyFiles).map(([fileKey, fileInfo]) => {
                    const displayName = fileInfo?.display_name || fileInfo?.name || fileKey;
                    const isPdf = (fileInfo?.name || '').toLowerCase().endsWith('.pdf');
                    return (
                      <div key={fileKey} data-testid={`company-file-${fileKey}`}
                        className="flex items-center justify-between py-2.5 px-3 bg-slate-50 rounded-lg text-sm">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPdf ? 'bg-red-100' : 'bg-blue-100'}`}>
                            {isPdf ? <FileText className="w-4 h-4 text-red-500" /> : <ImageIcon className="w-4 h-4 text-blue-500" />}
                          </div>
                          <span className="font-medium text-slate-700 truncate">{displayName}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => handleCompanyFileDownload(fileKey, fileInfo?.name || `${fileKey}.pdf`)}
                            data-testid={`download-company-file-${fileKey}`} className="h-7 px-2 text-[10px] gap-1">
                            <Download className="w-3 h-3" />{isRTL ? 'تحميل' : 'Download'}
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => handleCompanyFileDelete(fileKey)}
                              data-testid={`delete-company-file-${fileKey}`} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-slate-400 text-center py-3">{isRTL ? 'لا توجد ملفات' : 'No files'}</p>
                )}
                {isAdmin && (
                  <div className="pt-1">
                    {showCompanyCustomUpload ? (
                      <div className="p-3 border border-dashed border-blue-300 rounded-xl bg-white space-y-2.5">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700">{isRTL ? 'إضافة ملف' : 'Add File'}</p>
                          <Button size="sm" variant="ghost" onClick={() => { setShowCompanyCustomUpload(false); setCompanyCustomFileName(''); }} className="h-6 w-6 p-0">
                            <X className="w-3.5 h-3.5 text-slate-400" />
                          </Button>
                        </div>
                        <Input data-testid="company-custom-file-name" value={companyCustomFileName}
                          onChange={(e) => setCompanyCustomFileName(e.target.value)}
                          placeholder={isRTL ? 'اسم الملف (اختياري)' : 'File name (optional)'}
                          className="h-8 text-xs" dir={isRTL ? 'rtl' : 'ltr'} />
                        <Button size="sm" variant="outline" disabled={companyUploading}
                          data-testid="company-file-upload-trigger"
                          onClick={() => companyFileInputRef.current?.click()}
                          className="h-7 text-[10px] gap-1.5 w-full border-dashed border-blue-400 text-blue-600">
                          {companyUploading ? (
                            <><div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> {isRTL ? 'جاري الرفع...' : 'Uploading...'}</>
                          ) : (
                            <><Upload className="w-3 h-3" /> {isRTL ? 'اختر ملف' : 'Choose File'}</>
                          )}
                        </Button>
                        <input ref={companyFileInputRef} type="file" accept="image/*,.pdf" multiple
                          onChange={handleCompanyFileUpload} className="hidden" data-testid="company-file-upload-input" />
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" data-testid="add-company-file-btn"
                        onClick={() => setShowCompanyCustomUpload(true)}
                        className="w-full h-8 text-xs gap-1.5 border-dashed border-slate-300 text-slate-500 hover:text-slate-700">
                        <Plus className="w-3 h-3" /> {isRTL ? 'إضافة ملف' : 'Add File'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Company Price List (admin) */}
          {isAdmin && <CompanyPriceList company={selectedCompany} companies={companies} isRTL={isRTL} headers={authHeaders} />}

          {/* ============ EMPLOYEES GRID ============ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="employees-grid">
            {filteredEmployees.map((employee) => {
              const statusLabels = {
                completed: { ar: 'مكتملة', en: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
                pending: { ar: 'قيد الإجراء', en: 'In Progress', cls: 'bg-amber-100 text-amber-700' },
                cancelled: { ar: 'ملغية', en: 'Cancelled', cls: 'bg-red-100 text-red-700' }
              };
              const transStatus = employee.last_transaction_status ? statusLabels[employee.last_transaction_status] : null;

              return (
                <div
                  key={employee.id}
                  data-testid={`employee-card-${employee.id}`}
                  onClick={() => navigate(`/employees/${employee.id}`)}
                  className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-slate-300 transition-all duration-200"
                >
                  <div className={`flex items-start gap-3`}>
                    <EmployeePhoto employeeId={employee.id} employeeName={employee.name} size="md" />
                    <div className={`flex-1 min-w-0`}>
                      <h3 className="font-semibold text-slate-800 text-sm truncate group-hover:text-slate-900" data-testid={`employee-name-link-${employee.id}`}>
                        {employee.name}
                      </h3>
                      {employee.nationality && (
                        <p className="text-xs text-slate-400 mt-0.5 truncate">{employee.nationality}</p>
                      )}
                      {employee.passport_number && (
                        <p className="text-xs text-slate-400 font-mono mt-0.5" dir="ltr">{employee.passport_number}</p>
                      )}
                    </div>
                  </div>
                  {/* Footer */}
                  <div className={`flex items-center justify-between mt-3 pt-2.5 border-t border-slate-100`}>
                    {transStatus ? (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${transStatus.cls}`}>
                        {isRTL ? transStatus.ar : transStatus.en}
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-300">{isRTL ? 'لا توجد معاملات' : 'No transactions'}</span>
                    )}
                    {isAdmin && (
                      <div className={`flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                        <Button size="sm" variant="ghost" data-testid={`edit-employee-btn-${employee.id}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedEmployee(employee); setShowEditEmployee(true); }}
                          className="h-7 w-7 p-0 hover:bg-blue-50">
                          <Pencil className="w-3 h-3 text-blue-500" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-employee-btn-${employee.id}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedEmployee(employee); setShowDeleteConfirm(true); }}
                          className="h-7 w-7 p-0 hover:bg-red-50">
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredEmployees.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-slate-300" />
              </div>
              <p className="text-slate-500 text-sm font-medium">{t('noData')}</p>
              <p className="text-slate-300 text-xs mt-1">{t('addEmployee')}</p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <AddEmployeeModal isOpen={showAddEmployee} onClose={() => setShowAddEmployee(false)} onSave={handleSaveEmployee} isRTL={isRTL} t={t} existingEmployees={employees} />
      <EditEmployeeModal isOpen={showEditEmployee} onClose={() => { setShowEditEmployee(false); setSelectedEmployee(null); }} onSave={handleEditEmployee} employee={selectedEmployee} isRTL={isRTL} t={t} />
      <DeleteConfirmModal isOpen={showDeleteConfirm} onClose={() => { setShowDeleteConfirm(false); setSelectedEmployee(null); }} onConfirm={handleDeleteEmployee} employeeName={selectedEmployee?.name} isRTL={isRTL} t={t} />
    </div>
  );
};
